import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {fetchAvailability, upsertAvailability} from '../endpoints/api';
import useAuth from './useAuth';

export interface AvailabilityData {
  general: string[];
  specific: Record<string, string[]>;
  updated_at?: string;
}

export const useAvailability = () => {
  const {user} = useAuth();
  const userId = user?.id;

  return useQuery<AvailabilityData>({
    queryKey: ['availability', userId],
    queryFn: async () => {
      if (!userId) return {general: [], specific: {}};
      const res = await fetchAvailability(userId);
      const data = (res as any)?.data?.data || (res as any)?.data;
      return {
        general: data?.general || [],
        specific: data?.specific || {},
        updated_at: data?.updated_at,
      };
    },
    enabled: !!userId,
  });
};

export const useUpsertAvailability = () => {
  const {user} = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ['availability', user?.id];

  return useMutation({
    mutationFn: (data: {general: string[]; specific: Record<string, string[]>}) => {
      if (!user?.id) throw new Error('Not authenticated');
      return upsertAvailability(user.id, data);
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries(queryKey);
      const previous = queryClient.getQueryData<AvailabilityData>(queryKey);
      queryClient.setQueryData<AvailabilityData>(queryKey, (old) => ({
        general: data.general,
        specific: data.specific,
        updated_at: old?.updated_at,
      }));
      return {previous};
    },
    onError: (_err, _data, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKey);
    },
  });
};
